export type SortDirection = 'asc' | 'desc';
export type SortField = { field: string; direction: SortDirection };
export type MultiSortState = { sortFields: SortField[] };

/**
 * Calculate the next multi-sort state based on current sorting and clicked field
 * @param currentSortFields Current array of sort fields
 * @param clickedField Field that was clicked
 * @returns New sort state with updated sort fields
 */
export function getNextMultiSortState(currentSortFields: SortField[], clickedField: string): MultiSortState {
  // Find if the clicked field is already in the sort array
  const existingFieldIndex = currentSortFields.findIndex(sf => sf.field === clickedField);

  // Multi-column sorting (default and only behavior)
  const newSortFields = [...currentSortFields];

  if (existingFieldIndex === -1) {
    // Add new field to the end with ascending direction
    newSortFields.push({ field: clickedField, direction: 'asc' });
  } else {
    const currentField = newSortFields[existingFieldIndex];
    if (currentField.direction === 'asc') {
      // Change to descending
      newSortFields[existingFieldIndex] = { field: clickedField, direction: 'desc' };
    } else {
      // Remove this field from sorting
      newSortFields.splice(existingFieldIndex, 1);
    }
  }

  return { sortFields: newSortFields };
}

/**
 * Convert multi-sort state to backend format (comma-separated strings)
 * @param sortFields Array of sort fields
 * @returns Object with sortBy and sortDirection as comma-separated strings or null
 */
export function multiSortToBackendFormat(sortFields: SortField[]): {
  sortBy: string | null;
  sortDirection: string | null;
} {
  if (sortFields.length === 0) {
    return { sortBy: null, sortDirection: null };
  }

  const sortBy = sortFields.map(sf => sf.field).join(',');
  const sortDirection = sortFields.map(sf => sf.direction).join(',');

  return { sortBy, sortDirection };
}

/**
 * Convert backend format to multi-sort state (comma-separated strings to array)
 * @param sortBy Comma-separated field names
 * @param sortDirection Comma-separated directions
 * @returns Array of sort fields
 */
export function backendFormatToMultiSort(sortBy: string | null, sortDirection: string | null): SortField[] {
  if (!sortBy || !sortDirection) {
    return [];
  }

  const fields = sortBy.split(',');
  const directions = sortDirection.split(',');

  return fields.map((field, index) => ({
    field: field.trim(),
    direction: (directions[index]?.trim() as SortDirection) || 'asc',
  }));
}

/**
 * Create a multi-sort click handler for table headers
 * @param headers Array of header definitions with field and sortable properties
 * @param currentSortFields Current array of sort fields
 * @param updateSorting Function to update sorting state
 * @returns Click handler function
 */
export function createMultiSortClickHandler(
  headers: Array<{ field: string; sortable: boolean }>,
  currentSortFields: SortField[],
  updateSorting: (sortBy: string | null, sortDirection: string | null) => void,
) {
  return (event: React.MouseEvent<HTMLButtonElement>) => {
    const { field } = event.currentTarget.dataset;
    const header = headers.find(el => el.field === field && el.sortable);

    if (header && field) {
      const { sortFields } = getNextMultiSortState(currentSortFields, field);
      const { sortBy, sortDirection } = multiSortToBackendFormat(sortFields);
      updateSorting(sortBy, sortDirection);
    }
  };
}

/**
 * Get sort indicator for a specific field
 * @param field Field name
 * @param sortFields Current sort fields
 * @returns Object with direction and order (for multi-column sorting visual indicators)
 */
export function getSortIndicator(
  field: string,
  sortFields: SortField[],
): { direction: SortDirection | null; order: number | null } {
  const fieldIndex = sortFields.findIndex(sf => sf.field === field);

  if (fieldIndex === -1) {
    return { direction: null, order: null };
  }

  return {
    direction: sortFields[fieldIndex].direction,
    order: sortFields.length > 1 ? fieldIndex + 1 : null, // Show order number only for multi-column sorting
  };
}
